const MAX_BYTES = 20 * 1024 * 1024
const MAX_MEGAPIXELS = 20 * 1_000_000
const MIN_ASPECT = 1 / 100
const MAX_ASPECT = 100

const MIME_ALLOW = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
  "image/gif",
])

const EXT_ALLOW = new Set(["jpg", "jpeg", "png", "webp", "heic", "heif", "gif"])

export interface ThemeImageDimensionCheck {
  ok: boolean
  messageKey?: string
}

const extensionOf = (filename: string): string => {
  const parts = filename.toLowerCase().split(".")
  if (parts.length < 2) {
    return ""
  }
  return parts[parts.length - 1] || ""
}

export const isThemeImageMimeOrExtensionAllowed = (file: File): boolean => {
  if (file.type && MIME_ALLOW.has(file.type)) {
    return true
  }
  const ext = extensionOf(file.name)
  return EXT_ALLOW.has(ext)
}

const loadImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error("image-load-failed"))
    }
    img.src = url
  })
}

export const validateThemeImageFile = async (file: File): Promise<ThemeImageDimensionCheck> => {
  if (file.name.startsWith(".")) {
    return { ok: false, messageKey: "ThemeImageValidationFilename" }
  }

  if (!isThemeImageMimeOrExtensionAllowed(file)) {
    return { ok: false, messageKey: "ThemeImageValidationType" }
  }

  if (file.size > MAX_BYTES) {
    return { ok: false, messageKey: "ThemeImageValidationSize" }
  }

  let width = 0
  let height = 0

  try {
    const dims = await loadImageDimensions(file)
    width = dims.width
    height = dims.height
  } catch {
    return { ok: true }
  }

  if (width <= 0 || height <= 0) {
    return { ok: true }
  }

  const pixels = width * height
  if (pixels > MAX_MEGAPIXELS) {
    return { ok: false, messageKey: "ThemeImageValidationMegapixels" }
  }

  const aspect = width / height
  if (aspect < MIN_ASPECT || aspect > MAX_ASPECT) {
    return { ok: false, messageKey: "ThemeImageValidationAspect" }
  }

  return { ok: true }
}

export const toSafeShopifyFilename = (file: File): string => {
  const raw = file.name.trim() || "image.jpg"
  if (raw.startsWith(".")) {
    return `file-${raw.replace(/^\.+/, "")}`
  }
  return raw
}
